import {
  Component,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DateRange, DefaultMatCalendarRangeStrategy, MatCalendar, MatDatepickerModule, MatRangeDateSelectionModel } from '@angular/material/datepicker';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { CustomDateAdapter } from './custom-adapter';
import { CalendarHeaderComponent } from './calendar-header/calendar-header.component';

@Component({
  selector: 'so-calendar-ui',
  templateUrl: './so-calendar.component.html',
  styleUrls: ['./so-calendar.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
    },
    DefaultMatCalendarRangeStrategy,
    MatRangeDateSelectionModel,
  ],
  imports: [MatDatepickerModule, MatNativeDateModule],
  standalone: true,
})
export class SoCalendarUI {
  private readonly matRangeDateSelectionModel = inject(MatRangeDateSelectionModel);
  private readonly defaultMatCalendarRangeStrategy = inject(DefaultMatCalendarRangeStrategy);

  @ViewChild('picker', { static: false }) _picker!: MatCalendar<Date>;

  selectionMode = input<'single' | 'range'>('range');
  selectedDateRange = input<DateRange<Date> | null>(null);
  selectedDate = input<Date | null>(null);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);
  onChangeDateRange = output<DateRange<Date> | null>();
  onChangeDate = output<Date | null>();

  private lastExternalRangeKey = signal<string | null>(null);
  private lastExternalDateKey = signal<number | null>(null);

  private selectedDateRangeState = signal<DateRange<Date> | null>(null);
  private selectedDateState = signal<Date | null>(null);

  public initialDate = signal<Date>(new Date());
  public calendarHeader = CalendarHeaderComponent;

  protected effectiveSelectedDateRange = computed(() => this.selectedDateRangeState() ?? this.selectedDateRange());
  protected effectiveSelectedDate = computed(() => this.selectedDateState() ?? this.selectedDate());

  constructor() {
    effect(() => {
      if (this.selectionMode() !== 'range') return;

      const range = this.selectedDateRange();
      const startMs = range?.start instanceof Date ? range.start.getTime() : null;
      const endMs = range?.end instanceof Date ? range.end.getTime() : null;
      const key = `${startMs ?? ''}|${endMs ?? ''}`;
      if (key === this.lastExternalRangeKey()) return;

      this.lastExternalRangeKey.set(key);
      this.selectedDateRangeState.set(range);
      const base = range?.start ? new Date(range.start) : new Date();
      this.initialDate.set(new Date(base));

      queueMicrotask(() => {
        const d = this.initialDate();
        if (this._picker && d) {
          this._picker.activeDate = d;
          this._picker.focusActiveCell();
        }
      });
    }, {  });

    effect(() => {
      if (this.selectionMode() !== 'single') return;

      const current = this.selectedDate();
      const key = current instanceof Date ? current.getTime() : null;
      if (key === this.lastExternalDateKey()) return;

      this.lastExternalDateKey.set(key);
      this.selectedDateState.set(current ? new Date(current) : null);
      const base = current ? new Date(current) : new Date();
      this.initialDate.set(new Date(base));

      queueMicrotask(() => {
        const d = this.initialDate();
        if (this._picker && d) {
          this._picker.activeDate = d;
          this._picker.focusActiveCell();
        }
      });
    }, {  });
  }

  rangeChanged(selectedDate: Date | null): void {
    if (this.selectionMode() !== 'range') return;
    const selection = this.matRangeDateSelectionModel.selection;
    const newSelection = this.defaultMatCalendarRangeStrategy.selectionFinished(
      selectedDate,
      selection,
    );
    this.matRangeDateSelectionModel.updateSelection(newSelection, this);
    const next = new DateRange<Date>(newSelection.start, newSelection.end);
    this.selectedDateRangeState.set(next);
    this.onChangeDateRange.emit(next);
  }

  dateChanged(date: Date | null): void {
    if (this.selectionMode() !== 'single') return;

    const candidate = date ?? this._picker?.selected ?? null;
    const nextSelection = candidate instanceof Date ? candidate : null;

    const next = nextSelection ? new Date(nextSelection) : null;
    this.selectedDateState.set(next);
    this.onChangeDate.emit(next);
  }
}
